/**
 * Patch the chat's persisted "Document created / updated" card metadata in the
 * live message buffer after a document is renamed or moved.
 *
 * Name saving failure front end (#330). The durable fix is server-side: `GET
 * /conversations/<id>` resolves every card event to the LIVE document row before
 * serializing, so a reload is correct by construction. But a conversation SWITCH
 * repaints synchronously from `convMessagesRef` (the warm buffer) and only
 * reconciles with the network fetch a moment later — which would flash the stale
 * name in between. Patching the buffer closes that window, and keeps a card in a
 * conversation the user is not looking at correct too.
 *
 * Pure: returns a NEW messages array only when something actually changed (so a
 * caller can skip the state write and the re-render), and never mutates its
 * input — the buffer entries are shared with rendered React state.
 */

const LIST_KEYS = ['document_creates', 'document_edits'];
const SCALAR_KEYS = [
    'document_create_complete',
    'document_edit_complete',
    'document_create',
    'document_edit',
];
const NAME_KEYS = ['document_name', 'filename'];

/**
 * @param {object} event one persisted card event
 * @param {{documentId: string, name?: string, folderId?: string|null}} change
 * @returns {object} the same object when it does not reference the document,
 *                   otherwise a patched copy
 */
function patchEvent(event, change) {
    if (!event || typeof event !== 'object') return event;
    const id = event.document_id || event.source_document_id;
    if (!id || String(id) !== String(change.documentId)) return event;

    let next = null;
    if (change.name) {
        for (const key of NAME_KEYS) {
            if (key in event && event[key] !== change.name) {
                next = next || { ...event };
                next[key] = change.name;
            }
        }
    }
    if ('folderId' in change && change.folderId !== undefined && event.folder_id !== change.folderId) {
        next = next || { ...event };
        next.folder_id = change.folderId;
    }
    return next || event;
}

function patchMetadata(meta, change) {
    if (!meta || typeof meta !== 'object') return meta;
    let next = null;

    for (const key of LIST_KEYS) {
        const list = meta[key];
        if (!Array.isArray(list)) continue;
        const patched = list.map((ev) => patchEvent(ev, change));
        if (patched.some((ev, i) => ev !== list[i])) {
            next = next || { ...meta };
            next[key] = patched;
        }
    }
    for (const key of SCALAR_KEYS) {
        const ev = meta[key];
        if (!ev || typeof ev !== 'object') continue;
        const patched = patchEvent(ev, change);
        if (patched !== ev) {
            next = next || { ...meta };
            next[key] = patched;
        }
    }
    return next || meta;
}

/**
 * @param {object[]} messages a conversation's UI messages
 * @param {{documentId: string, name?: string, folderId?: string|null}} change
 * @returns {object[]} the SAME array when nothing referenced the document
 */
export function patchDocumentCards(messages, change) {
    if (!Array.isArray(messages) || !change?.documentId) return messages;
    let changed = false;
    const next = messages.map((msg) => {
        const meta = msg?.metadata;
        const patchedMeta = patchMetadata(meta, change);
        if (patchedMeta === meta) return msg;
        changed = true;
        return { ...msg, metadata: patchedMeta };
    });
    return changed ? next : messages;
}

export default patchDocumentCards;
